import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import styled from '@emotion/styled';
import { type FavoriteService, useEditFavoriteService } from '@kc-console/context';
import { useTranslation } from '@kc-console/core';
import { IcAddLargeRegular } from '@kep/kcds-icons';

import { useOpenFavoriteServiceModal, useRefinedFavoriteServiceState } from '../../../../hooks';
import { TooltipIconServiceButton, TooltipServiceButton } from '../ServiceButton';
import FavoriteBodyDropTarget from './FavoriteBodyDropTarget';
import FavoriteDragLayer from './FavoriteDragLayer';
import FavoriteList from './FavoriteList';
import FavoriteMoreButtonContainer from './FavoriteMoreButtonContainer';
import FavoriteMoreList from './FavoriteMoreList';
import { useFavoriteDndReorder } from './useFavoriteDndReorder';
import { isSameOrderByName, splitByOverflow } from './utils';

/**
 * 즐겨찾기 서비스 컨테이너 컴포넌트
 * @description
 * - 즐겨찾기 등록 버튼 또는 즐겨찾기 서비스 목록 표시
 * - 화면 너비를 초과하는 항목은 우측 Dropdown 메뉴로 이동
 * - ResizeObserver를 사용하여 컨테이너 크기를 감지하며 넘어가는 항목을 분리
 * - 드래그 앤 드롭으로 즐겨찾기 순서를 변경 (노출 목록 ↔ 더보기 메뉴 교차 이동 지원)
 */
const FavoriteContainer = () => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLUListElement>(null);
  const wrapperRef = useRef<HTMLLIElement>(null);
  const itemRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const moreButtonRef = useRef<HTMLButtonElement>(null);

  const { favoriteServices = [] } = useRefinedFavoriteServiceState();
  const { mutate: editFavoriteService } = useEditFavoriteService();
  const openFavoriteServiceModal = useOpenFavoriteServiceModal();

  const [visibleItems, setVisibleItems] = useState<FavoriteService[]>([]);
  const [hiddenItems, setHiddenItems] = useState<FavoriteService[]>([]);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);

  const closeMoreMenu = useCallback(() => {
    setIsMoreMenuOpen(false);
  }, []);

  // 드롭 커밋 후 이동한 아이템이 실제로 메뉴(숨김 영역)에 표시될지 예측한다.
  // checkOverflow와 동일한 함수·폭 입력으로 reflow 결과를 미리 계산하므로 정확하다.
  // (경계 드롭은 인디케이터가 메뉴 쪽에 보여도 reflow 후 탑바 끝에 남을 수 있어 표시 위치로 판정해야 함)
  const willDisplayInMenu = useCallback((next: FavoriteService[], movedName: string) => {
    const container = containerRef.current;
    if (!container) return false;
    const { hidden } = splitByOverflow({
      items: next,
      availableWidth: container.clientWidth,
      getItemWidth: service => itemRefs.current.get(service.name)?.offsetWidth ?? 0,
    });
    return hidden.some(service => service.name === movedName);
  }, []);

  const {
    favoriteListDropGap,
    handleDragEnd,
    handleDragStart,
    handleDragHover,
    handleMoreListDrop,
    localOrder,
    moreListDropGap,
    setIsOutsideDropZone,
  } = useFavoriteDndReorder({
    favoriteServices,
    listRef: containerRef,
    onCommitOrder: editFavoriteService,
    onCloseMoreMenu: closeMoreMenu,
    visibleCount: visibleItems.length,
    willDisplayInMenu,
  });

  const hasMoreItems = useMemo(() => {
    return hiddenItems.length > 0;
  }, [hiddenItems]);

  const hasFavoriteServices = useMemo(() => {
    return localOrder.length > 0;
  }, [localOrder]);

  const handleOpenAddModal = useCallback(() => {
    openFavoriteServiceModal({
      initialSelected: localOrder.map(service => service.name),
    });
  }, [openFavoriteServiceModal, localOrder]);

  const checkOverflow = useCallback(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    // strict prefix 분할(visible=[0..k-1]/hidden=[k..]) — 불변식 설명은 splitByOverflow TSDoc 참고
    const { visible, hidden } = splitByOverflow({
      items: localOrder,
      availableWidth: container.clientWidth,
      getItemWidth: service => itemRefs.current.get(service.name)?.offsetWidth ?? 0,
    });

    // 즐겨찾기 보여지는 항목에 변화가 있을 때만 업데이트
    setVisibleItems(prevVisible => {
      if (!isSameOrderByName(prevVisible, visible)) {
        return visible;
      }
      return prevVisible;
    });

    // 즐겨찾기 더보기 항목에 변화가 있을 때만 업데이트
    setHiddenItems(prevHidden => {
      if (!isSameOrderByName(prevHidden, hidden)) {
        return hidden;
      }
      return prevHidden;
    });
  }, [localOrder]);

  useEffect(() => {
    if (!hasFavoriteServices) {
      setVisibleItems([]);
      setHiddenItems([]);
      return;
    }

    const rafId = requestAnimationFrame(checkOverflow);
    const containerObserver = new ResizeObserver(checkOverflow);

    if (containerRef.current) {
      containerObserver.observe(containerRef.current);
    }

    return () => {
      cancelAnimationFrame(rafId);
      containerObserver.disconnect();
    };
  }, [localOrder, hasFavoriteServices, checkOverflow]);

  return (
    // context=globalThis 필수(제거 금지). react-dnd v16 DndProvider는 매니저를 globalThis에
    // 캐싱하지만 context 미지정(isGlobalInstance=true) 시 unmount cleanup이 그 캐시를 null로 지운다.
    // 로컬 dev는 react-dnd가 인스턴스 2개로 로드되고(layout vs hoisted peer-hash 사본) StrictMode
    // 이중 마운트로 이 즐겨찾기 Provider의 cleanup이 공유 캐시를 null → 컬럼편집(Select reordering)
    // 메뉴 Provider가 두 번째 HTML5Backend를 setup → "Cannot have two HTML5 backends" 오류.
    // context 지정 시 그 null-cleanup을 건너뛰어 단일 매니저/Backend가 공유된다(prod은 무관).
    <DndProvider backend={HTML5Backend} context={globalThis}>
      <FavoriteDragLayer />
      <FavoriteBodyDropTarget barRef={wrapperRef} onOutsideChange={setIsOutsideDropZone} />
      <LiWrapper ref={wrapperRef} onDragStartCapture={handleDragStart}>
        {!hasFavoriteServices && (
          <TooltipServiceButton
            message={t('layout:favorite.button.add.tooltip')}
            startIcon={<IcAddLargeRegular />}
            onClick={handleOpenAddModal}
          >
            {t('layout:favorite.button.add')}
          </TooltipServiceButton>
        )}
        {hasFavoriteServices && (
          <>
            <FavoriteList
              favoriteItems={localOrder}
              itemRefs={itemRefs}
              visibleItems={visibleItems}
              onDragHover={handleDragHover}
              dropIndicatorGap={favoriteListDropGap}
              onDragEnd={handleDragEnd}
              ref={containerRef}
            >
              <li>
                <AddIconButton
                  message={t('layout:favorite.button.add.tooltip')}
                  onClick={handleOpenAddModal}
                >
                  <IcAddLargeRegular />
                </AddIconButton>
              </li>
            </FavoriteList>
            {hasMoreItems && (
              <FavoriteMoreButtonContainer
                ref={moreButtonRef}
                boundaryIndex={visibleItems.length}
                onDragHover={handleDragHover}
                setIsMoreMenuOpen={setIsMoreMenuOpen}
              >
                <FavoriteMoreList
                  anchorEl={moreButtonRef.current}
                  favoriteMoreItems={hiddenItems}
                  allItems={localOrder}
                  isMoreMenuOpen={isMoreMenuOpen}
                  setIsMoreMenuOpen={setIsMoreMenuOpen}
                  onDragHover={handleDragHover}
                  dropIndicatorGap={moreListDropGap}
                  onDragEnd={handleDragEnd}
                  onDrop={handleMoreListDrop}
                />
              </FavoriteMoreButtonContainer>
            )}
          </>
        )}
      </LiWrapper>
    </DndProvider>
  );
};

export default FavoriteContainer;

const LiWrapper = styled('li')`
  display: flex;
  align-items: center;
  width: 100%;
  overflow: hidden;
`;

const AddIconButton = styled(TooltipIconServiceButton)`
  flex-shrink: 0;
  min-width: 40px;
`;
